import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Box, Flex, Input, Text } from '@chakra-ui/react'
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion'
import { useForm } from 'react-hook-form'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { LuArrowRight, LuBuilding, LuCheck, LuEye, LuEyeOff, LuLock, LuScanLine, LuUser } from 'react-icons/lu'
import { useAuth } from '../../contexts/AuthContext'
import { getErrorMessage } from '../../api/errorCodes'
import { getTenantCodeFromSubdomain } from '../../api/tenant'
import { useT } from '../../i18n'
import { brand, fonts, mineral, SECTION_ACCENTS } from '../../theme'
import KunturMark from '../../components/brand/KunturMark'
import Contours from '../../components/brand/Contours'
import RouteFlow from '../../components/brand/RouteFlow'

interface FormData {
  tenantCode: string
  username: string
  password: string
}

/** El recorrido que Kuntur traza: cada etapa con su mineral. */
const RECORRIDO = [
  { label: 'Mina', color: SECTION_ACCENTS.origin!.color },
  { label: 'Traslado', color: SECTION_ACCENTS.logistics!.color },
  { label: 'Planta', color: SECTION_ACCENTS.plant!.color },
  { label: 'Cierre', color: SECTION_ACCENTS.closeout!.color },
]

const VETA = `linear-gradient(90deg, ${mineral.cobre}, ${mineral.oro}, ${mineral.turquesa}, ${mineral.lapis}, ${mineral.rodocrosita}, ${mineral.cobre})`

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuth()
  const { t } = useT()
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [welcome, setWelcome] = useState(false)
  const [celebrating, setCelebrating] = useState(false)
  const card = useAnimationControls()

  useEffect(() => {
    // Durante la bienvenida la navegación la hace el propio flujo de éxito.
    if (isAuthenticated && !celebrating) navigate('/app/dashboard', { replace: true })
  }, [isAuthenticated, celebrating, navigate])

  // Código de organización: del subdominio (acme.kuntur.com.pe) o KUNTUR. Siempre de solo lectura.
  const subdomainTenant = useMemo(() => getTenantCodeFromSubdomain(), [])
  const effectiveTenant = subdomainTenant ?? 'KUNTUR'

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    defaultValues: { tenantCode: effectiveTenant },
  })

  const onSubmit = async (data: FormData) => {
    setIsLoading(true)
    setLoginError(null)
    setCelebrating(true)
    const result = await login(effectiveTenant, data.username, data.password)

    if (result.success) {
      // La bienvenida existe porque el acceso fue aceptado: el cóndor despega y entra.
      setWelcome(true)
      setTimeout(() => navigate('/app/dashboard'), 900)
      return
    }
    setCelebrating(false)
    setLoginError(getErrorMessage(result.errorCode, result.message))
    setIsLoading(false)
    // Rechazado: la tarjeta "niega con la cabeza".
    void card.start({ x: [0, -12, 10, -7, 5, 0], transition: { duration: 0.45 } })
  }

  const onInvalid = () => {
    void card.start({ x: [0, -8, 6, -3, 0], transition: { duration: 0.35 } })
  }

  return (
    <Flex minH="100vh" direction="column" position="relative" overflow="hidden" bg={mineral.bosque950}>
      {/* Foto de mina, más oscura y con el relieve encima */}
      <Box position="absolute" inset={0} backgroundImage="url('/fondo_main.jpg')" backgroundSize="cover" backgroundPosition="center"
        style={{ filter: 'saturate(0.85)' }} />
      <Box position="absolute" inset={0}
        style={{ background: `radial-gradient(70% 60% at 18% 28%, ${mineral.ocreDeep}55 0%, transparent 60%), radial-gradient(90% 70% at 20% 30%, ${mineral.bosque700}cc 0%, ${mineral.bosque900}ee 55%, ${mineral.bosque950} 100%)` }} />
      <Contours color="rgba(233, 162, 27, 0.10)" peaks={[[160, 220], [560, 520], [760, 120]]} rings={11} strokeWidth={1.1} />
      {/* El sistema en movimiento: camiones de bocamina a planta, garitas y expedientes sellándose */}
      <RouteFlow />

      <Flex flex={1} position="relative" zIndex={1} align="center" justify="center" px={{ base: 4, md: 10 }} py={{ base: 8, md: 12 }}>
        <Flex w="100%" maxW="1180px" align="center" justify="space-between" gap={12} direction={{ base: 'column', lg: 'row' }}>
          {/* Declaración de marca */}
          <Box flex={1} color="white" maxW="560px" textAlign={{ base: 'center', lg: 'left' }}>
            <motion.div
              animate={welcome ? { y: -80, scale: 1.25, opacity: 0 } : { y: 0, scale: 1, opacity: 1 }}
              transition={{ duration: 0.8, ease: [0.5, 0, 0.2, 1] }}
              style={{ display: 'inline-block', color: mineral.ocreLight }}
            >
              <KunturMark width={120} animate />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.6 }}>
              <Text fontFamily={fonts.display} fontWeight={800} fontSize={{ base: '56px', md: '84px' }} lineHeight="0.9" letterSpacing="-0.045em" mt={4}>
                Kuntur
              </Text>
              <Text fontSize={{ base: '17px', md: '20px' }} color="rgba(255,255,255,0.78)" mt={4} lineHeight="1.45" maxW="460px" mx={{ base: 'auto', lg: 0 }}>
                Trazabilidad minera de la bocamina a la planta. Cada gramo con su origen, cada documento con su sello.
              </Text>
            </motion.div>

            <Flex display={{ base: 'none', md: 'flex' }} mt={9} align="center" gap={0} justify={{ base: 'center', lg: 'flex-start' }}>
              {RECORRIDO.map((r, i) => (
                <Flex key={r.label} align="center">
                  <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.75 + i * 0.12, type: 'spring', stiffness: 400, damping: 22 }}>
                    <Flex align="center" gap={2} px={3} py={1.5} borderRadius="full" bg="rgba(255,255,255,0.07)" border="1px solid rgba(255,255,255,0.12)" backdropFilter="blur(6px)">
                      <Box w="8px" h="8px" borderRadius="2px" transform="rotate(45deg)" bg={r.color} boxShadow={`0 0 10px ${r.color}`} />
                      <Text fontSize="13px" fontWeight={600} color="rgba(255,255,255,0.88)">{r.label}</Text>
                    </Flex>
                  </motion.div>
                  {i < RECORRIDO.length - 1 && (
                    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.85 + i * 0.12, duration: 0.4 }}
                      style={{ width: 26, height: 1.5, transformOrigin: 'left', background: `linear-gradient(90deg, ${r.color}, ${RECORRIDO[i + 1]!.color})` }} />
                  )}
                </Flex>
              ))}
            </Flex>
          </Box>

          {/* Tarjeta de acceso */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{ width: '100%', maxWidth: 440 }}
          >
            <motion.div animate={card}>
              <Box position="relative" borderRadius="24px" overflow="hidden" bg="rgba(255,255,255,0.1)" backdropFilter="blur(22px) saturate(1.3)"
                border="1px solid rgba(255,255,255,0.18)" boxShadow="0 40px 80px -24px rgba(0,0,0,0.6)">
                {/* La veta: recorre los minerales de Kuntur; se acelera mientras valida */}
                <motion.div
                  animate={{ backgroundPositionX: ['0%', '200%'] }}
                  transition={{ duration: isLoading ? 1.1 : 9, repeat: Infinity, ease: 'linear' }}
                  style={{ height: isLoading ? 4 : 3, backgroundImage: VETA, backgroundSize: '200% 100%', transition: 'height .2s' }}
                />

                <AnimatePresence mode="wait">
                  {welcome ? (
                    <motion.div key="welcome" initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.35 }}>
                      <Flex direction="column" align="center" textAlign="center" px={8} py={16} color="white">
                        <motion.div initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 380, damping: 16 }}>
                          <Flex w="64px" h="64px" borderRadius="20px" align="center" justify="center" bg={mineral.bosque500} boxShadow={`0 0 0 8px ${mineral.bosque500}33`}>
                            <LuCheck size={32} />
                          </Flex>
                        </motion.div>
                        <Text fontFamily={fonts.display} fontWeight={800} fontSize="28px" mt={6} letterSpacing="-0.02em">Bienvenido</Text>
                        <Text color="rgba(255,255,255,0.7)" mt={1}>Preparando tu tablero…</Text>
                      </Flex>
                    </motion.div>
                  ) : (
                    <motion.div key="form" exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.2 }}>
                      <Box as="form" onSubmit={handleSubmit(onSubmit, onInvalid)} px={{ base: 6, md: 9 }} pt={8} pb={8} {...({ noValidate: true } as object)}>
                        <Text fontFamily={fonts.display} fontWeight={800} fontSize="28px" color="white" letterSpacing="-0.02em">
                          {t('login.accessTitle')}
                        </Text>
                        <Text fontSize="14px" color="rgba(255,255,255,0.68)" mt={1}>{t('login.accessSubtitle')}</Text>

                        <Flex direction="column" gap={4} mt={7}>
                          <Field icon={<LuBuilding size={13} />} label={t('login.organizationLabel')}>
                            <Box position="relative">
                              <Input {...register('tenantCode')} value={effectiveTenant} readOnly aria-readonly tabIndex={-1}
                                h="48px" borderRadius="12px" bg="rgba(255,255,255,0.06)" border="1px dashed rgba(255,255,255,0.25)"
                                color="white" fontFamily={fonts.mono} fontWeight={600} letterSpacing="0.08em" cursor="not-allowed" pl={4}
                                _focus={{ boxShadow: 'none' }} _focusVisible={{ boxShadow: 'none' }} />
                              <Box position="absolute" right="14px" top="50%" transform="translateY(-50%)" color="rgba(255,255,255,0.45)"><LuLock size={14} /></Box>
                            </Box>
                          </Field>

                          <Field icon={<LuUser size={13} />} label={t('login.userLabel')} error={errors.username?.message}>
                            <Input {...register('username', { required: t('login.userRequired') })} placeholder={t('login.userPlaceholder')}
                              autoComplete="username" {...inputSx(!!errors.username)} />
                          </Field>

                          <Field icon={<LuLock size={13} />} label={t('login.passwordLabel')} error={errors.password?.message}>
                            <Box position="relative">
                              <Input {...register('password', { required: t('login.passwordRequired'), minLength: { value: 6, message: t('login.passwordMinLength') } })}
                                type={showPassword ? 'text' : 'password'} placeholder={t('login.passwordPlaceholder')} autoComplete="current-password"
                                pr="46px" {...inputSx(!!errors.password)} />
                              <Box as="button" {...{ type: 'button' }} position="absolute" right="10px" top="50%" transform="translateY(-50%)" p={1.5}
                                borderRadius="8px" color={mineral.ink3} _hover={{ color: mineral.ink, bg: 'rgba(0,0,0,0.05)' }}
                                onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                                {showPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                              </Box>
                            </Box>
                          </Field>
                        </Flex>

                        <AnimatePresence>
                          {loginError && (
                            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}>
                              <Flex mt={4} gap={2.5} align="flex-start" p={3} borderRadius="12px" bg="rgba(214, 64, 43, 0.18)" border="1px solid rgba(255, 140, 120, 0.35)">
                                <Box w="6px" h="6px" mt="7px" borderRadius="full" bg="#ff8a73" flexShrink={0} />
                                <Text fontSize="13.5px" color="#ffd6cd">{loginError}</Text>
                              </Flex>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        <motion.button
                          type="submit"
                          disabled={isLoading}
                          initial="rest"
                          animate={isLoading ? 'loading' : 'rest'}
                          whileHover={isLoading ? undefined : 'hover'}
                          whileTap={isLoading ? undefined : { scale: 0.985 }}
                          style={{
                            marginTop: 24, width: '100%', height: 52, borderRadius: 14, border: 0, position: 'relative', overflow: 'hidden',
                            background: brand.primary, color: 'white', cursor: isLoading ? 'wait' : 'pointer',
                            fontFamily: fonts.display, fontWeight: 700, fontSize: 16, letterSpacing: '0.01em', padding: 0,
                          }}
                        >
                          {/* Dos colores con significado: terracota = la acción, pestaña teal = "ir".
                              Quieto en reposo. Al pasar el cursor la pestaña crece y la flecha avanza;
                              al salir vuelve con el MISMO resorte (sin saltos). Al enviar, el teal cubre
                              el botón de derecha a izquierda mientras valida; si falla, se retira. */}
                          <motion.span
                            variants={{
                              rest: { width: 56 },
                              hover: { width: 68 },
                              loading: { width: '100%' },
                            }}
                            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
                            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, background: mineral.bosque600, borderTopLeftRadius: 12, borderBottomLeftRadius: 12, overflow: 'hidden' }}
                          >
                            {isLoading && (
                              <motion.span
                                animate={{ backgroundPositionX: ['0px', '56px'] }}
                                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
                                style={{ position: 'absolute', inset: 0, backgroundImage: 'repeating-linear-gradient(-60deg, transparent 0 14px, rgba(255,255,255,0.09) 14px 28px)' }}
                              />
                            )}
                          </motion.span>
                          <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', paddingRight: isLoading ? 0 : 56 }}>
                            {isLoading ? 'Verificando credenciales…' : 'Ingresar'}
                          </span>
                          {!isLoading && (
                            <motion.span
                              variants={{ rest: { x: 0 }, hover: { x: 4 } }}
                              transition={{ type: 'spring', stiffness: 320, damping: 26 }}
                              style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: 56, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <LuArrowRight size={19} />
                            </motion.span>
                          )}
                        </motion.button>

                        <Flex mt={6} pt={5} borderTop="1px solid rgba(255,255,255,0.12)" align="center" justify="space-between" gap={3}>
                          <Text fontSize="12.5px" color="rgba(255,255,255,0.6)">¿Tienes un documento de Kuntur?</Text>
                          <Box as={RouterLink as never} {...({ to: '/verificar' } as object)} display="inline-flex" alignItems="center" gap={1.5}
                            fontSize="12.5px" fontWeight={600} color={mineral.oro} _hover={{ color: '#ffc95a' }}>
                            <LuScanLine size={14} /> Verificar
                          </Box>
                        </Flex>
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Box>
            </motion.div>
          </motion.div>
        </Flex>
      </Flex>

      <Flex as="footer" position="relative" zIndex={1} py={4} px={{ base: 4, md: 10 }} direction={{ base: 'column', md: 'row' }}
        justify="space-between" align="center" gap={3} borderTop="1px solid rgba(255,255,255,0.08)" bg="rgba(0,0,0,0.2)" backdropFilter="blur(8px)">
        <Text fontFamily={fonts.mono} fontSize="11px" letterSpacing="0.14em" color="rgba(255,255,255,0.55)">KUNTUR · TRAZABILIDAD MINERAL</Text>
        <Flex gap={6} flexWrap="wrap" justify="center">
          <FooterLink to="/politica-privacidad">{t('login.privacyPolicy')}</FooterLink>
          <FooterLink href="mailto:soporte@kuntur.com.pe">{t('login.support')}</FooterLink>
          <FooterLink href="https://kuntur.com.pe">{t('login.userGuide')}</FooterLink>
        </Flex>
        <Text fontFamily={fonts.mono} fontSize="11px" color="rgba(255,255,255,0.45)">v{__APP_VERSION__}</Text>
      </Flex>
    </Flex>
  )
}

function inputSx(invalid: boolean) {
  return {
    h: '48px',
    borderRadius: '12px',
    bg: 'rgba(255,255,255,0.92)',
    border: '1px solid',
    borderColor: invalid ? '#ff8a73' : 'transparent',
    color: mineral.ink,
    fontSize: '15px',
    px: 4,
    _placeholder: { color: mineral.ink4 },
    _focus: { outline: 'none', bg: 'white', boxShadow: `0 0 0 4px ${invalid ? 'rgba(255,138,115,0.35)' : 'rgba(227,168,77,0.45)'}` },
    _focusVisible: { outline: 'none' },
    transition: 'box-shadow .18s, background .18s',
  } as const
}

function Field({ icon, label, error, children }: { icon: ReactNode; label: string; error?: string; children: ReactNode }) {
  return (
    <Box>
      <Flex align="center" gap={2} mb={1.5} color="rgba(255,255,255,0.75)">
        {icon}
        <Text fontFamily={fonts.mono} fontSize="10.5px" letterSpacing="0.14em">{label}</Text>
      </Flex>
      {children}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <Text color="#ffb4a3" fontSize="12px" mt={1.5}>{error}</Text>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  )
}

function FooterLink({ to, href, children }: { to?: string; href?: string; children: string }) {
  const sx = { fontSize: '11px', fontWeight: 600, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.72)', _hover: { color: 'white' }, transition: 'color .2s' }
  return to
    ? <Box as={RouterLink as never} {...({ to } as object)} {...sx}>{children}</Box>
    : <Box as="a" {...({ href, target: href?.startsWith('http') ? '_blank' : undefined, rel: 'noreferrer' } as object)} {...sx}>{children}</Box>
}
